import { FC, ReactNode, useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ExpandMore } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import { List, Paper, Typography, Divider, Box, ListItemButton, Chip, Grid, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'

import GRCircularProgress from '../../../../../components/GRCircularProgress/GRCircularProgress'
import { endOfDayTimestamp } from '../../../../../helpers/endOfDayTimestamp'
import languageService from '../../../../../services/LanguageService'
import { useInternalAccessCheck } from '../../../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../../../account/hooks/useCurrentUserLanguage'
import { useCurrentMarket } from '../../../../markets'
import { useLiveEventArchetypeDiscontinued } from '../../../hooks/liveEventsArchetypes'
import { useLiveEventTagGroupsMapByEventTypeId } from '../../../hooks/useLiveEventTagGroups'
import { useTrackedEvents } from '../../../hooks/useTrackedEvents'
import { LiveEventTagGroup } from '../../../types/LiveEventTagGroup'
import { TrackedGame } from '../../../types/TrackedGame'
import { TrackingEvent } from '../../../types/TrackingEvents'
import styles from './AllEventsTab.module.scss'

/**
 * Component representing event dialog All events tab
 */
type AllEventsTabProps = {
  currentTrackedGame?: TrackedGame
  currentEventTypeId?: string
  currentEventId?: string
  onEventSelect: (event: TrackingEvent) => void
  children?: ReactNode
}

export const AllEventsTab: FC<AllEventsTabProps> = ({ currentTrackedGame, onEventSelect, currentEventTypeId, currentEventId, children }) => {
  const { currentMarketIso } = useCurrentMarket()
  const { t } = useTranslation()
  const gameIds = useMemo(() => (currentTrackedGame ? [currentTrackedGame?.game.id] : []), [currentTrackedGame])
  const trackedEventsQuery = useTrackedEvents({ gameIds: gameIds, startTimestamp: 0, endTimestamp: endOfDayTimestamp(), marketIso: currentMarketIso })
  const liveEventTagGroupsMapByEventTypeId = useLiveEventTagGroupsMapByEventTypeId()
  const userLanguage = useCurrentUserLanguage()
  const isInternalUser = useInternalAccessCheck()

  // Memoize the filtered events to avoid recalculating on every render
  const filteredEvents = useMemo(() => {
    const events = trackedEventsQuery.data?.events || {}
    const filtered: TrackingEvent[] = []

    Object.values(events).forEach((gameEvents) => {
      gameEvents.forEach((event) => {
        const tagGroup = liveEventTagGroupsMapByEventTypeId[event.typeId]
        if (tagGroup && tagGroup.id !== LiveEventTagGroup.DEFINING_OCCURRENCES) {
          filtered.push(event)
        }
      })
    })

    return filtered
  }, [trackedEventsQuery.data?.events, liveEventTagGroupsMapByEventTypeId])

  // Memoize archetype name lookup to avoid repeated array searches
  const archetypeNamesMap = useMemo(() => {
    const map: { [archetypeId: string]: string } = {}
    if (currentTrackedGame?.archetypes) {
      currentTrackedGame.archetypes.forEach((archetype) => {
        map[archetype.archetypeId] = archetype.names[userLanguage] || archetype.names['en'] || ''
      })
    }
    return map
  }, [currentTrackedGame?.archetypes, userLanguage])

  // all other events grouped by event type
  const eventsGroupedByType = useMemo(() => {
    const typeEvents: { [eventTypeId: string]: TrackingEvent[] } = {}
    const uniqueEventsMap: { [eventId: string]: TrackingEvent } = {}

    // First pass: collect unique events without archetype data
    filteredEvents.forEach((event) => {
      if (!event.archetypeEvolutionData) {
        uniqueEventsMap[event.eventId] = event
      }
    })

    // Second pass: group by type
    Object.values(uniqueEventsMap).forEach((event) => {
      if (!typeEvents[event.typeId]) {
        typeEvents[event.typeId] = []
      }
      typeEvents[event.typeId].push(event)
    })

    return typeEvents
  }, [filteredEvents])

  // for non-internal users: all events grouped by event type (ignoring archetype categorization)
  const allEventsGroupedByType = useMemo(() => {
    const typeEvents: { [eventTypeId: string]: TrackingEvent[] } = {}
    const uniqueEventsMap: { [eventId: string]: TrackingEvent } = {}

    // First pass: collect all unique events (both with and without archetype data)
    filteredEvents.forEach((event) => {
      uniqueEventsMap[event.eventId] = event
    })

    // Second pass: group by type
    Object.values(uniqueEventsMap).forEach((event) => {
      if (!typeEvents[event.typeId]) {
        typeEvents[event.typeId] = []
      }
      typeEvents[event.typeId].push(event)
    })

    return typeEvents
  }, [filteredEvents])

  const eventsGroupedByTypeAndArchetype = useMemo(() => {
    if (!isInternalUser) {
      return {}
    }

    const typeEvents: { [eventTypeId: string]: { archetypeEvents: { [archetypeId: string]: TrackingEvent[] }; individualEvents: TrackingEvent[] } } = {}

    Object.entries(allEventsGroupedByType).forEach(([eventTypeId, events]) => {
      typeEvents[eventTypeId] = {
        archetypeEvents: {},
        individualEvents: [],
      }
      events.forEach((event) => {
        if (event.archetypeEvolutionData) {
          // if the archetypeEvents object does not have the archetypeId, create it
          if (!typeEvents[eventTypeId].archetypeEvents[event.archetypeEvolutionData.archetypeId]) {
            typeEvents[eventTypeId].archetypeEvents[event.archetypeEvolutionData.archetypeId] = []
          }
          typeEvents[eventTypeId].archetypeEvents[event.archetypeEvolutionData.archetypeId].push(event)
        } else {
          typeEvents[eventTypeId].individualEvents.push(event)
        }
      })
    })

    // Create sorted version of typeEvents
    const sortedTypeEvents: { [eventTypeId: string]: { archetypeEvents: { [archetypeId: string]: TrackingEvent[] }; individualEvents: TrackingEvent[] } } = {}

    // Sort eventTypeIds by EventName (using language service translation)
    const sortedEventTypeEntries = Object.entries(typeEvents).sort(([eventTypeIdA], [eventTypeIdB]) => {
      const nameA = languageService.getTranslation('tags', eventTypeIdA)
      const nameB = languageService.getTranslation('tags', eventTypeIdB)
      return nameA.localeCompare(nameB)
    })

    // Process each event type
    sortedEventTypeEntries.forEach(([eventTypeId, events]) => {
      sortedTypeEvents[eventTypeId] = {
        archetypeEvents: {},
        individualEvents: [],
      }

      // Sort archetypeEvents by archetypeName
      const sortedArchetypeEntries = Object.entries(events.archetypeEvents).sort(([archetypeIdA], [archetypeIdB]) => {
        const nameA = archetypeNamesMap[archetypeIdA] || ''
        const nameB = archetypeNamesMap[archetypeIdB] || ''
        return nameA.localeCompare(nameB)
      })

      // Add sorted archetype events
      sortedArchetypeEntries.forEach(([archetypeId, archetypeEvents]) => {
        // Sort events within each archetype by event name
        const sortedEvents = [...archetypeEvents].sort((a, b) => a.name.localeCompare(b.name))
        sortedTypeEvents[eventTypeId].archetypeEvents[archetypeId] = sortedEvents
      })

      // Sort individualEvents by event name
      const sortedIndividualEvents = [...events.individualEvents].sort((a, b) => a.name.localeCompare(b.name))
      sortedTypeEvents[eventTypeId].individualEvents = sortedIndividualEvents
    })

    return sortedTypeEvents
  }, [isInternalUser, allEventsGroupedByType, archetypeNamesMap])

  // Memoize sorted events to avoid sorting on every render
  const sortedEventsGroupedByType = useMemo(() => {
    const sorted: { [eventTypeId: string]: TrackingEvent[] } = {}
    const eventsToSort = isInternalUser ? eventsGroupedByType : allEventsGroupedByType
    Object.entries(eventsToSort).forEach(([eventTypeId, events]) => {
      sorted[eventTypeId] = [...events].sort((a, b) => a.name.localeCompare(b.name))
    })
    return sorted
  }, [isInternalUser, eventsGroupedByType, allEventsGroupedByType])

  // extract events of selected event type and the rest
  const selectedEventTypeEvents = useMemo(() => {
    return currentEventTypeId ? sortedEventsGroupedByType[currentEventTypeId] || [] : []
  }, [currentEventTypeId, sortedEventsGroupedByType])

  const otherEventTypeEvents = useMemo(() => {
    const { [currentEventTypeId as string]: excluded, ...rest } = sortedEventsGroupedByType
    return rest
  }, [currentEventTypeId, sortedEventsGroupedByType])

  // Memoize sorted other event type entries
  const sortedOtherEventTypeEntries = useMemo(() => {
    return Object.entries(otherEventTypeEvents).sort(([eventTypeId1], [eventTypeId2]) => {
      const name1 = languageService.getTranslation('tags', eventTypeId1)
      const name2 = languageService.getTranslation('tags', eventTypeId2)
      return name1.localeCompare(name2)
    })
  }, [otherEventTypeEvents])

  const getArchetypeName = useCallback(
    (archetypeId: string) => {
      return archetypeNamesMap[archetypeId] || ''
    },
    [archetypeNamesMap]
  )

  const isLoading = trackedEventsQuery.isLoading || trackedEventsQuery.isFetching

  return isLoading ? (
    <GRCircularProgress />
  ) : (
    <>
      {/* Show archetype categorization only for internal users */}
      {isInternalUser && (
        <Box sx={{ mb: 3 }}>
          <>
            {Object.entries(eventsGroupedByTypeAndArchetype).map(([eventTypeId, events]) => {
              const archetypeEntries = Object.entries(events.archetypeEvents).filter(([, evts]) => evts && evts.length > 0)
              return (
                <Accordion key={eventTypeId}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h3">{languageService.getTranslation('tags', eventTypeId)}</Typography>
                  </AccordionSummary>
                  <Divider />
                  {archetypeEntries.length > 0 && (
                    <AccordionDetails className={styles.accordionDetailsTint}>
                      {archetypeEntries.map(([archetypeId, events], index) => {
                        return (
                          <Accordion key={archetypeId}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography variant="h3">{getArchetypeName(archetypeId)}</Typography>
                            </AccordionSummary>
                            <Divider />
                            <AccordionDetails className={styles.accordionDetailsTint}>
                              <EventTypeSection
                                currentTrackedGame={currentTrackedGame}
                                events={events}
                                eventTypeId={eventTypeId}
                                currentEventId={currentEventId}
                                onItemClick={onEventSelect}
                                key={eventTypeId}
                                isInternalUser={isInternalUser}
                                hideTitle={true}
                              />
                            </AccordionDetails>
                          </Accordion>
                        )
                      })}
                    </AccordionDetails>
                  )}
                  {events.individualEvents.length > 0 && (
                    <AccordionDetails className={styles.accordionDetailsTint}>
                      <Accordion key={`no-archetype`}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="h3">{t('live-events:no_archetype')}</Typography>
                        </AccordionSummary>
                        <Divider />
                        <AccordionDetails className={styles.accordionDetailsTint}>
                          <EventTypeSection
                            currentTrackedGame={currentTrackedGame}
                            events={events.individualEvents}
                            eventTypeId={eventTypeId}
                            currentEventId={currentEventId}
                            onItemClick={onEventSelect}
                            key={eventTypeId}
                            isInternalUser={isInternalUser}
                            hideTitle={true}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </AccordionDetails>
                  )}
                </Accordion>
              )
            })}
          </>
        </Box>
      )}

      {!isInternalUser && (
        <EventTypeSection
          events={selectedEventTypeEvents}
          eventTypeId={currentEventTypeId}
          currentEventId={currentEventId}
          onItemClick={onEventSelect}
          isInternalUser={isInternalUser}
        />
      )}

      {!isInternalUser && sortedOtherEventTypeEntries.length > 0 && (
        <>
          <Divider sx={{ mt: 4, mb: 2 }}>
            <Trans i18nKey="live-events:event_dialog_all_events_other" />
          </Divider>
          {sortedOtherEventTypeEntries.map(([eventTypeId, events], index, array) => {
            return (
              <EventTypeSection
                events={events}
                eventTypeId={eventTypeId}
                currentEventId={currentEventId}
                onItemClick={onEventSelect}
                key={eventTypeId}
                isInternalUser={isInternalUser}
              />
            )
          })}
        </>
      )}
    </>
  )
}

type EventTypeSectionProps = {
  events: TrackingEvent[]
  currentTrackedGame?: TrackedGame
  eventTypeId?: string
  currentEventId?: string
  marginBottom?: number
  onItemClick: (event: TrackingEvent) => void
  isInternalUser: boolean
  hideTitle?: boolean
}

const EventTypeSection: FC<EventTypeSectionProps> = ({
  events,
  currentTrackedGame,
  eventTypeId,
  currentEventId,
  onItemClick,
  marginBottom = 3,
  isInternalUser,
  hideTitle = false,
}) => {
  const { t } = useTranslation()
  const isDiscontinued = useLiveEventArchetypeDiscontinued({
    archetypeId: events.length > 0 ? events[0].archetypeEvolutionData?.archetypeId : undefined,
    trackedGame: currentTrackedGame,
  })

  // Memoize the event type name to avoid repeated translation calls
  const eventTypeName = useMemo(() => {
    return eventTypeId ? languageService.getTranslation('tags', eventTypeId) : ''
  }, [eventTypeId])

  // Memoize the event click handler to prevent unnecessary re-renders
  const handleEventClick = useCallback(
    (event: TrackingEvent) => {
      onItemClick(event)
    },
    [onItemClick]
  )

  return (
    <>
      {eventTypeId && !hideTitle && (
        <Typography variant="h3" sx={{ mb: 2 }}>
          {eventTypeName}
        </Typography>
      )}
      <List component={Paper} disablePadding sx={{ mb: marginBottom }}>
        {events.map((event, index, list) => {
          return (
            <Box key={event.eventId}>
              <ListItemButton onClick={() => handleEventClick(event)}>
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <Typography variant="body1">{event.name}</Typography>
                  </Grid>
                  <Grid item>
                    <Grid container alignItems="center" gap={2}>
                      {currentEventId === event.eventId && <CheckIcon color="success" />}
                      {event.active === false && <Chip label={t('live-events:event_inactive')} color="warning" size="small" sx={{ m: 0 }} />}

                      {isInternalUser && isDiscontinued && (
                        <Chip label={t('live-events:live_event_evolution_type_discontinued')} color="error" size="small" sx={{ m: 0 }} />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </ListItemButton>

              {index < list.length - 1 && <Divider />}
            </Box>
          )
        })}
      </List>
    </>
  )
}
